import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { userAPI, adminAPI, companyAPI, reviewAPI, serviceCommentsAPI, stravaAPI, calendarAPI, authAPI } from '../services/api';
import Layout from '../components/Layout';
import BikeImageGallery from '../components/BikeImageGallery';
import BikeComments from '../components/BikeComments';
import Icon from '../components/Icon';


function BikeDetailPage({ user, onLogout, darkMode = true, onToggleDarkMode, onUserUpdate, overrideBikeId, forceEmbedded }) {
  const { bikeId: paramBikeId } = useParams();
  const bikeId = overrideBikeId ?? paramBikeId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmbedded = forceEmbedded || searchParams.get('embedded') === '1';
  const [bike, setBike] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [frameNumberInput, setFrameNumberInput] = useState('');
  const [savingFrameNumber, setSavingFrameNumber] = useState(false);
  const [transferRecipient, setTransferRecipient] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [validatingRecipient, setValidatingRecipient] = useState(false);
  const [recipientValid, setRecipientValid] = useState(null); // null = not validated, true = valid, false = invalid
  const [recipientUserInfo, setRecipientUserInfo] = useState(null);
  const [companyRegistrations, setCompanyRegistrations] = useState([]);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [reviewsByService, setReviewsByService] = useState({});
  const [notes, setNotes] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('passport'); // 'passport', 'strava', 'info', 'images', 'comments'
  // Strava state
  const [stravaStatus, setStravaStatus] = useState({ is_connected: false });
  const [stravaActivities, setStravaActivities] = useState([]);
  const [stravaStats, setStravaStats] = useState(null);
  const [stravaLoading, setStravaLoading] = useState(false);
  const [showStravaModal, setShowStravaModal] = useState(false);
  const [availableStravaActivities, setAvailableStravaActivities] = useState([]);
  const [stravaActivitiesLoading, setStravaActivitiesLoading] = useState(false);
  const [selectedStravaActivity, setSelectedStravaActivity] = useState(null);
  // Service comments state
  const [showServiceCommentsModal, setShowServiceCommentsModal] = useState(null);
  const [serviceComments, setServiceComments] = useState([]);
  const [newServiceComment, setNewServiceComment] = useState('');
  const [serviceCommentCategory, setServiceCommentCategory] = useState('general');
  const [loadingServiceComments, setLoadingServiceComments] = useState(false);
  // Review state
  const [showReviewModal, setShowReviewModal] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  // Calendar state
  const [showCalendarDropdown, setShowCalendarDropdown] = useState(null);
  const [calendarLinks, setCalendarLinks] = useState({});
  // Service detail modal state
  const [selectedService, setSelectedService] = useState(null);
  // Receipt upload state
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [extractingReceipt, setExtractingReceipt] = useState(false);
  const [extractedItems, setExtractedItems] = useState(null);
  const [showReceiptUpload, setShowReceiptUpload] = useState(false);
  // Receipt image modal state
  const [receiptModalImage, setReceiptModalImage] = useState(null);

  const isAdmin = user.role === 'admin' || user.role === 'company_admin' || user.role === 'super_admin';
  const isOwner = bike?.owner_id === user?.id;
  const isSquarePlaceholderVin = (vin) => (vin || '').trim().toLowerCase().startsWith('sq-ph-');
  const normalizedFrameNumber = bike && !isSquarePlaceholderVin(bike.vin) ? bike.vin : '';
  const frameNumberDisplay = normalizedFrameNumber || 'No frame number';

  useEffect(() => {
    fetchBike();
    if (!isAdmin) {
      fetchCompanyRegistrations();
    }
    fetchStravaStatus();
  }, [bikeId]);

  useEffect(() => {
    if (stravaStatus.is_connected && bikeId) {
      fetchBikeStravaActivities();
    }
  }, [stravaStatus.is_connected, bikeId]);

  const fetchStravaStatus = async () => {
    try {
      const response = await stravaAPI.getStatus();
      setStravaStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch Strava status', err);
    }
  };

  const fetchBikeStravaActivities = async () => {
    setStravaLoading(true);
    try {
      const response = await stravaAPI.getBikeActivities(bikeId);
      setStravaActivities(response.data.activities || []);
      setStravaStats(response.data.stats || null);
    } catch (err) {
      console.error('Failed to fetch Strava activities', err);
    } finally {
      setStravaLoading(false);
    }
  };

  const handleConnectStrava = async () => {
    try {
      const response = await stravaAPI.getAuthUrl();
      window.location.href = response.data.auth_url;
    } catch (err) {
      setError('Failed to initiate Strava connection');
    }
  };

  const handleOpenStravaModal = async () => {
    setShowStravaModal(true);
    setStravaActivitiesLoading(true);
    try {
      const response = await stravaAPI.getActivities({ per_page: 50 });
      // Filter out activities already linked to this bike
      const linkedIds = stravaActivities.map(a => a.strava_activity_id);
      const available = (response.data.activities || []).filter(
        a => !linkedIds.includes(a.id)
      );
      setAvailableStravaActivities(available);
    } catch (err) {
      setError('Failed to fetch Strava activities');
    } finally {
      setStravaActivitiesLoading(false);
    }
  };

  const handleAddStravaActivity = async (stravaActivityId) => {
    try {
      await stravaAPI.addActivityToBike(bikeId, stravaActivityId);
      setSuccess('Strava activity added to bike passport!');
      setShowStravaModal(false);
      fetchBikeStravaActivities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add activity');
    }
  };

  const handleRemoveStravaActivity = async (activityId) => {
    if (!window.confirm('Remove this Strava journey from the bike passport?')) return;
    try {
      await stravaAPI.removeActivityFromBike(bikeId, activityId);
      setSuccess('Strava activity removed');
      fetchBikeStravaActivities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove activity');
    }
  };

  const handleAddToCalendar = async (serviceId) => {
    try {
      const response = await calendarAPI.generateLinks('service', serviceId);
      setCalendarLinks(prev => ({ ...prev, [serviceId]: response.data.links }));
      setShowCalendarDropdown(serviceId);
    } catch (err) {
      setError('Failed to generate calendar link');
    }
  };

  const handleDownloadICS = (serviceId) => {
    const links = calendarLinks[serviceId];
    if (!links?.ics_content) return;
    
    const blob = new Blob([links.ics_content], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `service-${serviceId}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowCalendarDropdown(null);
  };

  const fetchBike = async () => {
    try {
      const response = isAdmin ? await adminAPI.getBike(bikeId) : await userAPI.getMyBike(bikeId);
      setBike(response.data.bike);
      const loadedVin = response.data.bike?.vin;
      setFrameNumberInput(isSquarePlaceholderVin(loadedVin) ? '' : (loadedVin || ''));
      setNotes(response.data.bike?.notes || '');
      if (response.data.bike?.services) {
        response.data.bike.services.forEach(s => loadReviewsForService(s.id));
      }
    } catch (err) {
      setError('Failed to load bike details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    setNotesLoading(true);
    try {
      if (isAdmin) {
        await adminAPI.updateBike(bikeId, { notes });
      } else {
        await userAPI.updateBikeNotes(bikeId, notes);
      }
      setSuccess('Notes saved successfully!');
      setEditingNotes(false);
      fetchBike(); // Refresh to get updated note history
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save notes');
    } finally {
      setNotesLoading(false);
    }
  };

  const handleSaveFrameNumber = async () => {
    const nextVin = (frameNumberInput || '').trim();
    if (!nextVin) {
      setError('Frame number is required');
      return;
    }
    setSavingFrameNumber(true);
    try {
      await adminAPI.updateBike(bikeId, { vin: nextVin });
      setSuccess('Frame number updated successfully');
      setError('');
      await fetchBike();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update frame number');
    } finally {
      setSavingFrameNumber(false);
    }
  };

  const fetchCompanyRegistrations = async () => {
    try {
      const response = await userAPI.getBikeCompanyRegistrations(bikeId);
      setCompanyRegistrations(response.data.registrations || []);
    } catch (err) {
      console.error('Failed to load company registrations');
    }
  };

  const loadReviewsForService = async (serviceId) => {
    try {
      const resp = await reviewAPI.listForService(serviceId);
      setReviewsByService(prev => ({ ...prev, [serviceId]: resp.data.reviews || [] }));
    } catch (err) {
      console.error('Failed to load reviews', err);
    }
  };

  const handleSubmitReview = async (serviceId) => {
    if (reviewRating === 0) {
      setError('Please select a rating');
      return;
    }
    
    try {
      setSubmittingReview(true);
      await reviewAPI.createForService(serviceId, {
        rating: reviewRating,
        comment: reviewComment
      });
      setShowReviewModal(null);
      setReviewRating(0);
      setReviewComment('');
      setSuccess('Review submitted successfully!');
      // Reload reviews for this service
      await loadReviewsForService(serviceId);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  // Receipt upload handlers
  // Check if a file is an image (handles iOS edge cases where type may be empty)
  const isReceiptImageFile = (file) => {
    if (file.type && file.type.startsWith('image/')) return true;
    const ext = (file.name || '').split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp', 'tiff'].includes(ext);
  };

  // Normalize and optionally compress image before upload
  // CRITICAL: Always processes camera photos through canvas to fix EXIF orientation
  // Camera apps store photos rotated with an EXIF tag - canvas drawing fixes this
  const compressReceiptImage = (file) => {
    return new Promise((resolve) => {
      if (!isReceiptImageFile(file)) {
        resolve(file);
        return;
      }
      // Skip HEIC/HEIF - browser canvas can't handle them, backend will convert
      const ext = (file.name || '').split('.').pop().toLowerCase();
      if (['heic', 'heif'].includes(ext)) {
        resolve(file);
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const MAX_SIDE = 2560;
            let { width, height } = img;
            if (width > MAX_SIDE || height > MAX_SIDE) {
              if (width >= height) {
                height = Math.round(height * MAX_SIDE / width);
                width = MAX_SIDE;
              } else {
                width = Math.round(width * MAX_SIDE / height);
                height = MAX_SIDE;
              }
            }
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            // Preserve PNG for screenshots (lossless text quality)
            const isPng = file.type === 'image/png' || ext === 'png';
            const outputType = isPng ? 'image/png' : 'image/jpeg';
            const outputQuality = isPng ? undefined : 0.92;
            canvas.toBlob((blob) => {
              if (blob) {
                // Always use canvas output - it fixes EXIF orientation
                resolve(new File([blob], file.name, { type: outputType, lastModified: file.lastModified }));
              } else {
                resolve(file);
              }
            }, outputType, outputQuality);
          } catch {
            resolve(file);
          }
        };
        img.onerror = () => resolve(file);
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  const handleReceiptFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const compressed = await compressReceiptImage(file);
      setReceiptFile(compressed);
      // Create preview for images (handle iOS empty file.type)
      if (isReceiptImageFile(compressed)) {
        const reader = new FileReader();
        reader.onloadend = () => setReceiptPreview(reader.result);
        reader.readAsDataURL(compressed);
      } else {
        setReceiptPreview(null);
      }
      setExtractedItems(null);
    }
  };

  const handleExtractFromReceipt = async () => {
    if (!receiptFile) return;
    
    setExtractingReceipt(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('receipt', receiptFile);
      
      const response = await userAPI.extractFromReceipt(formData);
      const extracted = response.data.extracted_data;
      
      // Check if extraction returned meaningful data
      const hasItems = extracted?.service_items && extracted.service_items.length > 0;
      const hasTotal = extracted?.total_cost && extracted.total_cost > 0;
      
      if (!extracted || (!hasItems && !hasTotal)) {
        setError('AI could not read this receipt. Please try with a clearer photo (avoid glare and blur).');
        setExtractingReceipt(false);
        return;
      }
      
      setExtractedItems(extracted);
      const parts = [];
      if (hasItems) parts.push(`${extracted.service_items.length} item(s)`);
      if (hasTotal) parts.push(`total: ${extracted.currency || '£'}${extracted.total_cost.toFixed(2)}`);
      setSuccess(`Extracted: ${parts.join(', ')}`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('AI extraction error:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to extract data from receipt';
      if (err.code === 'ECONNABORTED' || errorMsg.includes('timeout')) {
        setError('Extraction timed out. Please try with a smaller or clearer photo.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setExtractingReceipt(false);
    }
  };

  const handleUploadReceipt = async () => {
    if (!receiptFile || !selectedService) return;
    
    setUploadingReceipt(true);
    try {
      const formData = new FormData();
      formData.append('receipt', receiptFile);
      formData.append('include_extraction', 'false');
      
      const response = await userAPI.uploadServiceReceipt(bikeId, selectedService.id, formData);
      
      // If we have extracted items, save them
      if (extractedItems?.service_items?.length > 0) {
        await userAPI.addServiceItems(bikeId, selectedService.id, {
          items: extractedItems.service_items,
          total_cost: extractedItems.total_cost
        });
      }
      
      setSuccess('Receipt uploaded successfully!');
      setShowReceiptUpload(false);
      setReceiptFile(null);
      setReceiptPreview(null);
      setExtractedItems(null);
      fetchBike(); // Refresh to get updated service data
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload receipt');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const clearReceiptUpload = () => {
    setReceiptFile(null);
    setReceiptPreview(null);
    setExtractedItems(null);
    setShowReceiptUpload(false);
  };

  const loadAvailableCompanies = async () => {
    try {
      const response = await companyAPI.getActiveCompanies();
      setAvailableCompanies(response.data.companies || []);
    } catch (err) {
      setError('Failed to load companies');
    }
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    if (!selectedCompanyId) return;
    try {
      await userAPI.addBikeToCompany(bikeId, parseInt(selectedCompanyId));
      setShowCompanyModal(false);
      setSelectedCompanyId('');
      fetchCompanyRegistrations();
      setSuccess('Bike added to company!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add bike');
    }
  };

  const handleToggleRegistration = async (reg) => {
    try {
      await userAPI.updateBikeCompanyRegistration(bikeId, reg.company_id, !reg.is_active);
      fetchCompanyRegistrations();
      setSuccess('Registration updated!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update');
    }
  };

  const handleRemoveRegistration = async (reg) => {
    if (!window.confirm(`Remove bike from ${reg.company_name}?`)) return;
    try {
      await userAPI.removeBikeCompanyRegistration(bikeId, reg.company_id);
      fetchCompanyRegistrations();
      setSuccess('Registration removed!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove');
    }
  };

  // Validate if recipient user exists
  const validateRecipient = async (identifier) => {
    if (!identifier || identifier.length < 3) {
      setRecipientValid(null);
      setRecipientUserInfo(null);
      return;
    }
    
    setValidatingRecipient(true);
    setRecipientValid(null);
    setRecipientUserInfo(null);
    
    try {
      // Check if it's an email or username
      const isEmail = identifier.includes('@');
      let result;
      
      if (isEmail) {
        result = await authAPI.checkEmail(identifier);
        // If available: false, the user exists
        if (!result.data.available) {
          setRecipientValid(true);
          setRecipientUserInfo({ type: 'email', identifier });
        } else {
          setRecipientValid(false);
          setError('No user found with this email address');
        }
      } else {
        result = await authAPI.checkUsername(identifier);
        // If available: false, the user exists
        if (!result.data.available) {
          setRecipientValid(true);
          setRecipientUserInfo({ type: 'username', identifier });
        } else {
          setRecipientValid(false);
          setError('No user found with this username');
        }
      }
    } catch (err) {
      setRecipientValid(false);
      setError('Failed to validate recipient');
    } finally {
      setValidatingRecipient(false);
    }
  };
  
  // Debounced recipient validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (transferRecipient && transferRecipient.length >= 3) {
        validateRecipient(transferRecipient);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [transferRecipient]);

  const handleTransfer = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate recipient first
    if (recipientValid !== true) {
      setError('Please enter a valid username or email of an existing user');
      return;
    }
    
    try {
      const response = await userAPI.transferBike(bikeId, transferRecipient);
      const transferredBookings = response.data?.transferred_bookings || 0;
      let msg = 'Bike transferred!';
      if (transferredBookings > 0) {
        msg += ` ${transferredBookings} upcoming booking(s) also transferred to the new owner.`;
      }
      setSuccess(msg);
      if (onUserUpdate) {
        try {
          const profileRes = await userAPI.getProfile();
          if (profileRes.data?.user) onUserUpdate(profileRes.data.user);
        } catch (_) {}
      }
      setTimeout(() => navigate('/dashboard'), 2500);
    } catch (err) {
      setError(err.response?.data?.error || 'Transfer failed');
      setRecipientValid(false);
    }
  };

  // Service comments functions
  const fetchServiceComments = async (serviceId) => {
    setLoadingServiceComments(true);
    try {
      const res = await serviceCommentsAPI.getComments(serviceId);
      setServiceComments(res.data.comments || []);
    } catch (err) {
      console.error('Failed to load service comments', err);
      setServiceComments([]);
    } finally {
      setLoadingServiceComments(false);
    }
  };

  const handleOpenServiceComments = async (serviceId) => {
    setShowServiceCommentsModal(serviceId);
    setNewServiceComment('');
    setServiceCommentCategory('general');
    await fetchServiceComments(serviceId);
  };

  const handleAddServiceComment = async (e) => {
    e.preventDefault();
    if (!newServiceComment.trim()) return;
    
    try {
      await serviceCommentsAPI.addComment(showServiceCommentsModal, {
        comment_text: newServiceComment,
        category: serviceCommentCategory
      });
      setNewServiceComment('');
      setSuccess('Comment added!');
      await fetchServiceComments(showServiceCommentsModal);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add comment');
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} pageTitle="Loading..." darkMode={darkMode} onToggleDarkMode={onToggleDarkMode} hideSidebar={isEmbedded}>
        <div className="loading">Loading bike details...</div>
      </Layout>
    );
  }

  if (!bike) {
    return (
      <Layout user={user} onLogout={onLogout} pageTitle="Bike Not Found" darkMode={darkMode} onToggleDarkMode={onToggleDarkMode} hideSidebar={isEmbedded}>
        <div className="error">{error || 'Bike not found'}</div>
        <Link to="/dashboard" className="btn btn-primary">Back to Dashboard</Link>
      </Layout>
    );
  }

  return (
    <Layout 
      user={user} 
      onLogout={onLogout}
      pageTitle={
        <>
          <Icon name="badge" color="primary" darkMode={darkMode} size={20} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
          Bike Passport: {bike.brand} {bike.model}
        </>
      }
      pageSubtitle={`Complete digital record · ${bike.year} · Frame: ${frameNumberDisplay}`}
      darkMode={darkMode} onToggleDarkMode={onToggleDarkMode} hideSidebar={isEmbedded}>
      {error && <div className="error" style={{marginBottom: '20px'}}>{error}</div>}
      {success && <div className="success" style={{marginBottom: '20px'}}>{success}</div>}
      {isAdmin && !normalizedFrameNumber && (
        <div style={{
          marginBottom: '20px',
          padding: '16px',
          borderRadius: '10px',
          border: '1px solid rgba(255,255,255,0.18)',
          background: 'rgba(255,255,255,0.05)'
        }}>
          <div style={{ color: '#ffffff', fontWeight: 600, marginBottom: '8px' }}>
            No frame number assigned yet
          </div>
          <div style={{ color: 'rgba(255,255,255,0.75)', marginBottom: '10px', fontSize: '14px' }}>
            Add the real frame number to finalize this bike passport.
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={frameNumberInput}
              onChange={(e) => setFrameNumberInput(e.target.value)}
              placeholder="Enter frame number"
              maxLength={17}
              style={{ minWidth: '220px' }}
            />
            <button className="btn btn-primary" onClick={handleSaveFrameNumber} disabled={savingFrameNumber}>
              {savingFrameNumber ? 'Saving...' : 'Save frame number'}
            </button>
          </div>
        </div>
      )}

      {/* Profile Image Header */}
      {bike.profile_image_url && (
        <div style={{
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '20px'
        }}>
          <img
            src={bike.profile_image_url}
            alt={`${bike.brand} ${bike.model}`}
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '16px',
              objectFit: 'cover',
              border: '3px solid rgba(61, 90, 32, 0.3)'
            }}
            onError={(e) => e.target.style.display = 'none'}
          />
          <div>
            <h1 style={{ margin: 0, color: '#ffffff', fontSize: '28px' }}>
              {bike.brand} {bike.model}
            </h1>
            <p style={{ margin: '4px 0 0 0', color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
              {bike.year} · Frame: {frameNumberDisplay}
            </p>
          </div>
        </div>
      )}

      {/* Stolen Bike Warning Banner */}
      {bike.is_stolen && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.1) 100%)',
          border: '2px solid rgba(239, 68, 68, 0.5)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <Icon name="warning" color="error" darkMode={darkMode} size={40} />
          <div>
            <h3 style={{ margin: '0 0 4px 0', color: '#ef4444', fontSize: '18px', fontWeight: '700' }}>
              STOLEN BIKE ALERT
            </h3>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
              This bike has been reported as stolen{bike.stolen_reported_at ? ` on ${new Date(bike.stolen_reported_at).toLocaleDateString('en-GB')}` : ''}.
              {isAdmin ? ' Please verify ownership before servicing.' : ''}
            </p>
          </div>
        </div>
      )}

      {/* Actions */}
      {!isAdmin && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <button 
            onClick={() => { setShowCompanyModal(true); loadAvailableCompanies(); }} 
            className="btn btn-primary"
          >
            <Icon name="companies" color="primary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
            Manage Service Shops
          </button>
          <button 
            onClick={() => setShowTransferModal(true)} 
            className="btn btn-secondary"
            style={{ 
              whiteSpace: 'nowrap', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis',
              minWidth: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Icon name="swap" color="primary" darkMode={darkMode} size={16} style={{ flexShrink: 0 }} />
            <span>Transfer</span>
          </button>
        </div>
      )}

      {/* Passport Header - Visual Element */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(61, 90, 32, 0.15) 0%, rgba(160, 200, 20, 0.08) 100%)',
        border: '2px solid rgba(61, 90, 32, 0.3)',
        borderRadius: '20px',
        padding: '24px',
        marginBottom: '28px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Icon 
          name="badge" 
          color="muted" 
          darkMode={darkMode} 
          size={80} 
          style={{ 
            position: 'absolute',
            top: '10px',
            right: '10px',
            opacity: 0.08,
            transform: 'rotate(-15deg)'
          }} 
        />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ 
            fontSize: '12px', 
            color: 'rgba(255,255,255,0.5)', 
            marginBottom: '8px',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: '600'
          }}>
            <Icon name="badge" color="secondary" darkMode={darkMode} size={12} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
            Official Bike Passport
          </div>
          <h2 style={{ 
            fontSize: '24px', 
            color: '#ffffff', 
            margin: '0 0 12px 0',
            fontWeight: '700'
          }}>
            {bike.brand} {bike.model}
          </h2>
          <div style={{
            display: 'flex',
            gap: '20px',
            flexWrap: 'wrap',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.6)'
          }}>
            <div><strong>Year:</strong> {bike.year || 'N/A'}</div>
            <div><strong>Frame Number:</strong> {frameNumberDisplay}</div>
            <div 
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => setActiveTab('passport')}
              title="Click to view services"
            >
              <strong>Services:</strong> {bike.service_count || 0}
            </div>
            <div><strong>Photos:</strong> {bike.images?.length || 0}</div>
            {bike.created_at && (
              <div><strong>Passport created:</strong> {new Date(bike.created_at).toLocaleString()}</div>
            )}
          </div>
          {/* Ownerships */}
          {(bike.owner_name || (bike.ownership_history && bike.ownership_history.length > 0)) && (
            <div style={{ marginTop: '12px', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
              <strong>Ownership:</strong>{' '}
              {bike.ownership_history && bike.ownership_history.length > 0 ? (
                <span>
                  {bike.ownership_history.map((t, i) => (
                    <span key={t.id}>
                      {i > 0 && ' · '}
                      {t.from_name} → {t.to_name}
                      {t.created_at && ` (${new Date(t.created_at).toLocaleDateString()})`}
                    </span>
                  ))}
                </span>
              ) : (
                bike.owner_name || '—'
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        paddingBottom: '12px',
        flexWrap: 'wrap'
      }}>
        {[
          { id: 'passport', label: 'Service History', iconName: 'badge', count: bike?.service_count || 0 },
          { id: 'strava', label: 'Strava History', iconName: 'bike', count: stravaActivities.length, isNew: true },
          { id: 'info', label: 'Details', iconName: 'details', count: undefined },
          { id: 'images', label: 'Photos', iconName: 'photos', count: bike?.images?.length || 0 },
          { id: 'comments', label: 'Notes', iconName: 'chat', count: undefined }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px',
              background: activeTab === tab.id ? 'rgba(61, 90, 32, 0.15)' : 'rgba(255,255,255,0.03)',
              border: activeTab === tab.id ? '1px solid rgba(61, 90, 32, 0.3)' : '1px solid rgba(255,255,255,0.06)',
              borderRadius: '10px',
              color: activeTab === tab.id ? '#3d5a20' : 'rgba(255,255,255,0.7)',
              fontSize: '14px',
              fontWeight: activeTab === tab.id ? '600' : '400',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Icon name={tab.iconName} color={activeTab === tab.id ? "nav_active" : "nav_inactive"} darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
            {tab.label}
            {tab.isNew && (
              <span style={{
                padding: '2px 6px',
                background: 'linear-gradient(135deg, #fc4c02 0%, #ff6b35 100%)',
                borderRadius: '8px',
                fontSize: '9px',
                fontWeight: '700',
                color: '#fff',
                marginLeft: '4px'
              }}>
                NEW
              </span>
            )}
            {tab.count !== undefined && tab.count > 0 && (
              <span style={{
                padding: '2px 6px',
                background: activeTab === tab.id ? 'rgba(61, 90, 32, 0.3)' : 'rgba(255,255,255,0.1)',
                borderRadius: '8px',
                fontSize: '11px'
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'passport' && (
        <>
          {/* Service History - Main Passport Content */}
          <div style={{
            background: darkMode ? 'var(--bg-overlay-light)' : 'rgba(0,0,0,0.02)',
            border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e5e7eb',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0, color: darkMode ? '#ffffff' : '#111827', fontSize: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Icon name="service" color="primary" darkMode={darkMode} size={20} style={{ verticalAlign: 'middle', marginRight: '10px' }} />
                Complete Service History
              </h2>
              {!isAdmin && (
                <Link to="/book" className="btn btn-primary" style={{ textDecoration: 'none', fontSize: '14px' }}>
                  <Icon name="calendar" color="primary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  Book New Service
                </Link>
              )}
            </div>

            {bike?.services && bike.services.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {bike.services.map(service => {
                  const serviceDate = service.service_date ? new Date(service.service_date) : null;
                  // No warning for completed services with future dates - it's fine if service was done before scheduled appointment
                  
                  const cardBg = darkMode ? 'rgba(255,255,255,0.03)' : '#fafafa';
                  const cardBorder = darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb';
                  const cardBgHover = darkMode ? 'rgba(255,255,255,0.05)' : '#f3f4f6';
                  const cardBorderHover = 'rgba(61, 90, 32, 0.2)';
                  return (
                    <div key={service.id} style={{
                      padding: '20px',
                      background: cardBg,
                      border: `1px solid ${cardBorder}`,
                      borderRadius: '14px',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = cardBgHover;
                      e.currentTarget.style.borderColor = cardBorderHover;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = cardBg;
                      e.currentTarget.style.borderColor = cardBorder;
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
                        <div>
                          <div style={{ fontSize: '18px', fontWeight: '600', color: darkMode ? '#ffffff' : '#111827', marginBottom: '4px' }}>
                            {service.items?.[0]?.name || service.items?.[0]?.item_name || service.description?.substring(0, 50) || 'Service'}
                          </div>
                          {serviceDate && (
                            <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>
                              <Icon name="calendar" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                              {serviceDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                              {service.service_time && (
                                <span style={{ marginLeft: '8px' }}>
                                  {String(service.service_time).substring(0, 5)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div style={{
                          padding: '6px 12px',
                          background: service.status === 'completed' 
                            ? 'rgba(34, 197, 94, 0.15)' 
                            : service.status === 'pending_payment'
                              ? 'rgba(245, 158, 11, 0.15)'
                              : 'rgba(251, 191, 36, 0.15)',
                          border: `1px solid ${
                            service.status === 'completed' 
                              ? 'rgba(34, 197, 94, 0.3)' 
                              : service.status === 'pending_payment'
                                ? 'rgba(245, 158, 11, 0.3)'
                                : 'rgba(251, 191, 36, 0.3)'
                          }`,
                          borderRadius: '20px',
                          color: service.status === 'completed' 
                            ? '#22c55e' 
                            : service.status === 'pending_payment'
                              ? '#f59e0b'
                              : '#fbbf24',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {service.status === 'pending_payment' ? 'Pending Payment' : (service.status || 'Pending')}
                        </div>
                      </div>

                      {service.description && (
                        <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginBottom: '12px', lineHeight: '1.6' }}>
                          {service.description}
                        </div>
                      )}

                      {/* Service Price */}
                      {service.total_cost && parseFloat(service.total_cost) > 0 && (
                        <div style={{ 
                          fontSize: '15px', 
                          color: '#ffffff', 
                          marginBottom: '12px',
                          fontWeight: '600'
                        }}>
                          <Icon name="payment" color="primary" darkMode={darkMode} size={15} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                          Total Cost: <span style={{ color: 'var(--lime-primary)' }}>£{parseFloat(service.total_cost).toFixed(2)}</span>
                        </div>
                      )}

                      {/* Service Items */}
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '8px', fontWeight: '600' }}>
                          SERVICE ITEMS:
                        </div>
                        {service.items && service.items.length > 0 ? (
                          <div style={{ 
                            border: '1px solid rgba(255,255,255,0.06)',
                            borderRadius: '10px',
                            overflow: 'hidden'
                          }}>
                            {/* Header row */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 50px 70px 70px',
                              gap: '6px',
                              padding: '6px 12px',
                              background: 'rgba(255,255,255,0.04)',
                              fontSize: '10px',
                              fontWeight: '600',
                              color: 'rgba(255,255,255,0.4)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              <span>Item</span>
                              <span style={{ textAlign: 'center' }}>Qty</span>
                              <span style={{ textAlign: 'right' }}>Unit</span>
                              <span style={{ textAlign: 'right' }}>Total</span>
                            </div>
                            {service.items.map((item, idx) => (
                              <div key={idx} style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 50px 70px 70px',
                                gap: '6px',
                                padding: '7px 12px',
                                borderTop: '1px solid rgba(255,255,255,0.04)',
                                fontSize: '12px'
                              }}>
                                <span style={{ color: 'rgba(255,255,255,0.8)' }}>{item.item_name || item.name || 'Item'}</span>
                                <span style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>{item.quantity || 1}</span>
                                <span style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'right' }}>
                                  {parseFloat(item.unit_price || 0).toFixed(2)}
                                </span>
                                <span style={{ color: '#22c55e', fontWeight: '600', textAlign: 'right' }}>
                                  {parseFloat(item.total_price || item.price || item.item_price || 0).toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ 
                            padding: '8px 12px', 
                            background: 'rgba(255,255,255,0.02)', 
                            borderRadius: '8px', 
                            fontSize: '13px',
                            color: 'rgba(255,255,255,0.4)',
                            fontStyle: 'italic'
                          }}>
                            No service items recorded
                          </div>
                        )}
                      </div>

                      {/* Company and Technician Info */}
                      <div style={{ 
                        marginTop: '12px',
                        paddingTop: '12px',
                        borderTop: '1px solid rgba(255,255,255,0.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        {service.company_name && (
                          <div style={{ 
                            fontSize: '13px', 
                            color: 'rgba(255,255,255,0.6)', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px'
                          }}>
                            <Icon name="companies" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                            Serviced by: <strong style={{ color: 'rgba(255,255,255,0.8)' }}>{service.company_name}</strong>
                          </div>
                        )}

                        {service.technician_name && (
                          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
                            <Icon name="technician" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                            Technician: <strong style={{ color: 'rgba(255,255,255,0.8)' }}>{service.technician_name}</strong>
                          </div>
                        )}

                        {service.service_time && (
                          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
                            <Icon name="schedule" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                            Time: <strong style={{ color: 'rgba(255,255,255,0.8)' }}>
                              {new Date(`2000-01-01T${service.service_time}`).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                            </strong>
                          </div>
                        )}
                      </div>

                      {/* Receipts and Calendar Actions */}
                      <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {service.receipts && service.receipts.length > 0 && service.receipts.map((receipt, idx) => {
                          const receiptUrl = receipt.file_url || receipt.url || receipt.path;
                          const isImage = receipt.file_type?.startsWith('image') || 
                            ['jpg', 'jpeg', 'png', 'gif', 'webp'].some(ext => receiptUrl?.toLowerCase().endsWith(ext));
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => isImage ? setReceiptModalImage(receiptUrl) : window.open(receiptUrl, '_blank')}
                              className="btn btn-secondary"
                              style={{ fontSize: '13px', padding: '6px 12px', cursor: 'pointer' }}
                            >
                              <Icon name="documents" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                              Receipt {idx + 1}
                            </button>
                          );
                        })}
                        
                        {/* Add to Calendar Button - Only show for future/pending services */}
                        {service.status !== 'completed' && (!service.service_date || new Date(service.service_date) >= new Date(new Date().setHours(0,0,0,0))) && (
                        <div style={{ position: 'relative' }}>
                          <button
                            onClick={() => handleAddToCalendar(service.id)}
                            className="btn btn-secondary"
                            style={{ fontSize: '13px', padding: '6px 12px' }}
                          >
                            <Icon name="calendar" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                            Add to Calendar
                          </button>
                          
                          {/* Calendar Dropdown */}
                          {showCalendarDropdown === service.id && calendarLinks[service.id] && (
                            <div 
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                marginTop: '4px',
                                background: '#1a1a3e',
                                border: '1px solid rgba(255,255,255,0.15)',
                                borderRadius: '10px',
                                padding: '8px',
                                zIndex: 100,
                                minWidth: '180px',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <a
                                href={calendarLinks[service.id].google}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '10px 12px',
                                  color: '#ffffff',
                                  textDecoration: 'none',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  transition: 'background 0.2s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                onClick={() => setShowCalendarDropdown(null)}
                              >
                                <Icon name="calendar" color="primary" darkMode={darkMode} size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                                Google Calendar
                              </a>
                              <a
                                href={calendarLinks[service.id].outlook}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '10px 12px',
                                  color: '#ffffff',
                                  textDecoration: 'none',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  transition: 'background 0.2s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                onClick={() => setShowCalendarDropdown(null)}
                              >
                                <Icon name="email" color="primary" darkMode={darkMode} size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                                Outlook
                              </a>
                              <button
                                onClick={() => handleDownloadICS(service.id)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '10px 12px',
                                  color: '#ffffff',
                                  background: 'transparent',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                  width: '100%',
                                  textAlign: 'left',
                                  transition: 'background 0.2s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                              >
                                <Icon name="download" color="primary" darkMode={darkMode} size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                                Download .ics
                              </button>
                              <div 
                                style={{ 
                                  borderTop: '1px solid rgba(255,255,255,0.1)', 
                                  margin: '4px 0', 
                                  paddingTop: '4px'
                                }}
                              />
                              <button
                                onClick={() => setShowCalendarDropdown(null)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '8px 12px',
                                  color: 'rgba(255,255,255,0.5)',
                                  background: 'transparent',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  width: '100%'
                                }}
                              >
                                Close
                              </button>
                            </div>
                          )}
                        </div>
                        )}
                      </div>

                      {/* Reviews Section */}
                      <div style={{ 
                        marginTop: '16px',
                        paddingTop: '16px',
                        borderTop: '1px solid rgba(255,255,255,0.1)'
                      }}>
                        {reviewsByService[service.id] && reviewsByService[service.id].length > 0 ? (
                          <div>
                            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '8px', fontWeight: '600' }}>
                              REVIEWS:
                            </div>
                            {reviewsByService[service.id].map((review, idx) => (
                              <div key={idx} style={{
                                padding: '10px',
                                background: 'rgba(255,255,255,0.03)',
                                borderRadius: '8px',
                                marginBottom: '8px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                  {Array.from({ length: review.rating }).map((_, i) => (
                                    <Icon key={i} name="star" color="warning" darkMode={darkMode} size={14} style={{ marginRight: '2px' }} />
                                  ))}
                                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                                    {new Date(review.created_at).toLocaleDateString('en-GB')}
                                  </span>
                                </div>
                                {review.comment && (
                                  <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
                                    {review.comment}
                                  </div>
                                )}
                                {review.company_response && (
                                  <div style={{
                                    marginTop: '8px',
                                    padding: '8px',
                                    background: 'rgba(34, 197, 94, 0.1)',
                                    border: '1px solid rgba(34, 197, 94, 0.2)',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    color: 'rgba(255,255,255,0.8)'
                                  }}>
                                    <strong>Response from {service.company_name}:</strong> {review.company_response}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : null}
                        
                        {/* Action Buttons Row */}
                        <div style={{ display: 'flex', gap: '8px', marginTop: reviewsByService[service.id]?.length > 0 ? '8px' : '0', flexWrap: 'wrap' }}>
                          {/* View Full Details Button */}
                          <button
                            onClick={() => setSelectedService(service)}
                            className="btn btn-secondary"
                            style={{ fontSize: '13px', padding: '8px 16px' }}
                          >
                            <Icon name="view" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                            View Full Details
                          </button>
                          
                          {/* Write Review Button - only for completed services in the past */}
                          {service.status === 'completed' && (
                            <button
                              onClick={() => {
                                setShowReviewModal(service.id);
                                setReviewRating(0);
                                setReviewComment('');
                              }}
                              className="btn btn-primary"
                              style={{ fontSize: '13px', padding: '8px 16px' }}
                            >
                              <Icon name="star" color="primary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                              Write a Review
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: 'rgba(255,255,255,0.5)'
              }}>
                <Icon name="service" color="muted" darkMode={darkMode} size={48} style={{ marginBottom: '16px' }} />
                <p>No service history yet</p>
                {!isAdmin && (
                  <Link to="/book" className="btn btn-primary" style={{ textDecoration: 'none', marginTop: '12px' }}>
                    <Icon name="calendar" color="primary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    Book First Service
                  </Link>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Strava History Tab */}
      {activeTab === 'strava' && (
        <div style={{
          background: 'var(--bg-overlay-light)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h2 style={{ margin: 0, color: '#ffffff', fontSize: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Icon name="bike" color="primary" darkMode={darkMode} size={20} customColor="#fc4c02" style={{ verticalAlign: 'middle', marginRight: '10px' }} />
              Strava Journey History
            </h2>
            {stravaStatus.is_connected && !isAdmin && (
              <button onClick={handleOpenStravaModal} className="btn btn-primary" style={{ 
                background: 'linear-gradient(135deg, #fc4c02 0%, #ff6b35 100%)',
                fontSize: '14px' 
              }}>
                + Add Strava Journey
              </button>
            )}
          </div>

          {!stravaStatus.is_connected ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              background: 'rgba(252, 76, 2, 0.05)',
              border: '1px solid rgba(252, 76, 2, 0.2)',
              borderRadius: '12px'
            }}>
              <Icon name="bike" color="primary" darkMode={darkMode} size={64} customColor="#fc4c02" style={{ marginBottom: '16px' }} />
              <h3 style={{ color: '#ffffff', marginBottom: '12px' }}>Connect with Strava</h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '20px', maxWidth: '400px', margin: '0 auto 20px' }}>
                Link your Strava account to track all your rides and journeys with this bike. 
                Your ride history will be saved in the bike passport and transferred when you sell the bike.
              </p>
              <button 
                onClick={handleConnectStrava}
                className="btn btn-primary"
                style={{ 
                  background: 'linear-gradient(135deg, #fc4c02 0%, #ff6b35 100%)',
                  border: 'none',
                  padding: '14px 28px',
                  fontSize: '16px'
                }}
              >
                <Icon name="verified" color="primary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                Connect Strava Account
              </button>
            </div>
          ) : stravaLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
              Loading Strava activities...
            </div>
          ) : stravaActivities.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: 'rgba(255,255,255,0.5)'
            }}>
              <Icon name="bike" color="muted" darkMode={darkMode} size={48} customColor="#fc4c02" style={{ marginBottom: '16px', opacity: 0.7 }} />
              <p>No Strava journeys linked yet</p>
              <p style={{ fontSize: '13px', marginTop: '8px' }}>
                Click "Add Strava Journey" to link your rides to this bike
              </p>
            </div>
          ) : (
            <>
              {/* Stats Summary */}
              {stravaStats && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: '12px',
                  marginBottom: '20px'
                }}>
                  {[
                    { label: 'Total Distance', value: `${stravaStats.total_distance_km} km`, iconName: 'map' },
                    { label: 'Total Time', value: `${stravaStats.total_time_hours} hrs`, iconName: 'schedule' },
                    { label: 'Elevation Gain', value: `${stravaStats.total_elevation_m} m`, iconName: 'terrain' },
                    { label: 'Rides', value: stravaStats.activity_count, iconName: 'bike' }
                  ].map((stat, idx) => (
                    <div key={idx} style={{
                      padding: '16px',
                      background: 'rgba(252, 76, 2, 0.08)',
                      border: '1px solid rgba(252, 76, 2, 0.2)',
                      borderRadius: '12px',
                      textAlign: 'center'
                    }}>
                      <Icon name={stat.iconName} color="primary" darkMode={darkMode} size={24} customColor="#fc4c02" style={{ marginBottom: '4px' }} />
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#fc4c02' }}>{stat.value}</div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Activity List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {stravaActivities.map(activity => (
                  <div key={activity.id} style={{
                    padding: '16px',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '12px',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                    e.currentTarget.style.borderColor = 'rgba(252, 76, 2, 0.3)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', marginBottom: '4px' }}>
                          {activity.activity_name}
                        </div>
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                          <Icon name="calendar" color="secondary" darkMode={darkMode} size={12} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                          {new Date(activity.activity_date).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'long', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{
                          padding: '4px 10px',
                          background: 'rgba(252, 76, 2, 0.15)',
                          border: '1px solid rgba(252, 76, 2, 0.3)',
                          borderRadius: '20px',
                          color: '#fc4c02',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          {activity.activity_type}
                        </span>
                        {!isAdmin && (
                          <button
                            onClick={() => handleRemoveStravaActivity(activity.id)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.15)',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              borderRadius: '6px',
                              color: '#ef4444',
                              padding: '4px 8px',
                              fontSize: '11px',
                              cursor: 'pointer'
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Activity Stats */}
                    <div style={{
                      display: 'flex',
                      gap: '20px',
                      flexWrap: 'wrap',
                      fontSize: '13px',
                      color: 'rgba(255,255,255,0.7)'
                    }}>
                      <div><Icon name="map" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} /><strong>{activity.distance_km} km</strong></div>
                      <div><Icon name="schedule" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} /><strong>{activity.moving_time_formatted}</strong></div>
                      {activity.total_elevation_gain > 0 && (
                        <div><Icon name="terrain" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} /><strong>{Math.round(activity.total_elevation_gain)} m</strong></div>
                      )}
                      <div><Icon name="rocket" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} /><strong>{activity.average_speed_kmh} km/h</strong> avg</div>
                      {activity.average_heartrate && (
                        <div><Icon name="favorite" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} /><strong>{Math.round(activity.average_heartrate)} bpm</strong></div>
                      )}
                      {activity.average_watts && (
                        <div><Icon name="info" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} /><strong>{Math.round(activity.average_watts)} W</strong></div>
                      )}
                    </div>

                    {/* View on Strava link */}
                    <div style={{ marginTop: '12px' }}>
                      <a 
                        href={activity.strava_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: '12px',
                          color: '#fc4c02',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        View on Strava →
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
      
      {activeTab === 'info' && (
        <>
          {/* Company Registrations */}
      {!isAdmin && (
        <div style={{
          background: 'var(--bg-overlay-light)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <h2 style={{ margin: '0 0 20px 0', color: '#ffffff', fontSize: '18px' }}>
            <Icon name="companies" color="primary" darkMode={darkMode} size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            Registered Service Shops
          </h2>
          {companyRegistrations.length > 0 ? (
            <div style={{ display: 'grid', gap: '12px' }}>
              {companyRegistrations.map(reg => (
                <div key={reg.id} style={{
                  padding: '16px',
                  background: reg.is_active ? 'rgba(16, 185, 129, 0.05)' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${reg.is_active ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255,255,255,0.05)'}`,
                  borderRadius: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <strong style={{ color: '#ffffff' }}>{reg.company_name}</strong>
                      <span style={{
                        padding: '3px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: reg.is_active ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255,255,255,0.1)',
                        color: reg.is_active ? '#4A8C18' : 'rgba(255,255,255,0.5)'
                      }}>
                        {reg.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {reg.registered_at && (
                      <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                        Registered: {new Date(reg.registered_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleToggleRegistration(reg)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '12px',
                        background: reg.is_active ? 'rgba(245, 158, 11, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                        color: reg.is_active ? '#fbbf24' : '#4A8C18'
                      }}
                    >
                      {reg.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleRemoveRegistration(reg)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '12px',
                        background: 'rgba(239, 68, 68, 0.15)',
                        color: '#f87171'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgba(255,255,255,0.5)' }}>No service shops registered. Click "Manage Service Shops" to add one.</p>
          )}
        </div>
      )}

      {/* Bike Notes Section */}
      <div style={{
        background: 'var(--bg-overlay-light)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, color: '#ffffff', fontSize: '18px' }}>
            <Icon name="write" color="primary" darkMode={darkMode} size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            Notes
          </h2>
          {!editingNotes && (
            <button 
              onClick={() => setEditingNotes(true)}
              className="btn btn-sm btn-secondary"
              style={{ padding: '6px 14px', fontSize: '13px' }}
            >
              <Icon name="edit" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              Edit
            </button>
          )}
        </div>
        
        {editingNotes ? (
          <div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="form-input"
              rows={4}
              placeholder="Add notes about your bike (maintenance reminders, modifications, etc.)..."
              style={{ width: '100%', marginBottom: '12px', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                onClick={handleSaveNotes} 
                className="btn btn-primary"
                disabled={notesLoading}
              >
                {notesLoading ? 'Saving...' : 'Save Notes'}
              </button>
              <button 
                onClick={() => {
                  setEditingNotes(false);
                  setNotes(bike?.notes || '');
                }} 
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            {notes ? (
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                {notes}
              </p>
            ) : (
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
                No notes yet. Click Edit to add notes about your bike.
              </p>
            )}
          </div>
        )}

        {/* Note History */}
        {bike?.note_history && bike.note_history.length > 0 && (
          <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <h4 style={{ margin: '0 0 12px 0', color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
              <Icon name="documents" color="secondary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              Note History
            </h4>
            <div style={{ display: 'grid', gap: '10px' }}>
              {bike.note_history.map((history, index) => (
                <div 
                  key={history.id || index}
                  style={{
                    padding: '12px',
                    background: 'rgba(255,255,255,0.02)',
                    borderRadius: '8px',
                    fontSize: '13px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: 'rgba(255,255,255,0.5)' }}>
                      {history.changed_by_name || history.changed_by_username || 'Ismeretlen'}
                    </span>
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
                      {new Date(history.changed_at).toLocaleString('en-GB')}
                    </span>
                  </div>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', whiteSpace: 'pre-wrap' }}>
                    {history.previous_notes || '(üres)'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
        </>
      )}

      {/* Images Tab */}
      {activeTab === 'images' && (
        <BikeImageGallery
          bikeId={parseInt(bikeId)}
          images={bike?.images || []}
          profileImage={bike?.profile_image}
          isAdmin={isAdmin}
          canEdit={isOwner || isAdmin}
          onRefresh={fetchBike}
        />
      )}

      {/* Comments Tab */}
      {activeTab === 'comments' && (
        <BikeComments
          bikeId={parseInt(bikeId)}
          isAdmin={isAdmin}
          currentUser={user}
        />
      )}


      {/* Company Modal */}
      {showCompanyModal && (
        <Modal onClose={() => setShowCompanyModal(false)} title="Add to Service Shop" darkMode={darkMode}>
          <form onSubmit={handleAddCompany}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
                Select Company
              </label>
              <select 
                className="form-input"
                value={selectedCompanyId} 
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                required
              >
                <option value="">-- Select --</option>
                {availableCompanies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="submit" className="btn btn-primary">Add</button>
              <button type="button" onClick={() => setShowCompanyModal(false)} className="btn btn-secondary">Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Transfer Modal */}
      {showTransferModal && (
        <Modal onClose={() => {
          setShowTransferModal(false);
          setError('');
          setTransferRecipient('');
          setRecipientValid(null);
          setRecipientUserInfo(null);
        }} title="Transfer Bike Passport" darkMode={darkMode}>
          <form onSubmit={handleTransfer}>
            <p style={{ 
              marginBottom: '16px', 
              padding: '12px', 
              background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', 
              borderRadius: '8px', 
              fontSize: '13px',
              color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'
            }}>
              <Icon name="info" color="info" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              Transfer this bike to another user. They will become the new owner.
            </p>
            
            {error && (
              <div style={{ 
                marginBottom: '16px', 
                padding: '12px', 
                borderRadius: '8px', 
                background: 'rgba(239, 68, 68, 0.1)', 
                border: '1px solid rgba(239, 68, 68, 0.3)', 
                color: '#ef4444',
                fontSize: '13px'
              }}>
                <Icon name="warning" color="error" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                {error}
              </div>
            )}
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: darkMode ? '#ffffff' : '#1a1a1a', 
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Recipient (username or email) *
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={transferRecipient}
                  onChange={(e) => {
                    setTransferRecipient(e.target.value);
                    setError('');
                    setRecipientValid(null);
                  }}
                  placeholder="Enter username or email"
                  required
                  style={{ 
                    width: '100%',
                    padding: '12px 40px 12px 16px',
                    borderRadius: '8px',
                    border: recipientValid === true 
                      ? '2px solid #4A8C18' 
                      : recipientValid === false 
                        ? '2px solid #ef4444' 
                        : darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)',
                    background: darkMode ? 'rgba(255,255,255,0.05)' : '#ffffff',
                    color: darkMode ? '#ffffff' : '#1a1a1a',
                    fontSize: '15px',
                    outline: 'none'
                  }}
                />
                {/* Validation indicator */}
                <div style={{ 
                  position: 'absolute', 
                  right: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)'
                }}>
                  {validatingRecipient && (
                    <Icon name="loading" color="info" darkMode={darkMode} size={18} />
                  )}
                  {!validatingRecipient && recipientValid === true && (
                    <Icon name="success" color="success" darkMode={darkMode} size={18} />
                  )}
                  {!validatingRecipient && recipientValid === false && (
                    <Icon name="warning" color="error" darkMode={darkMode} size={18} />
                  )}
                </div>
              </div>
              
              {/* Validation message */}
              {recipientValid === true && (
                <div style={{ 
                  marginTop: '8px', 
                  fontSize: '12px', 
                  color: '#4A8C18',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Icon name="success" color="success" darkMode={darkMode} size={12} />
                  User found! Ready to transfer.
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={recipientValid !== true || validatingRecipient}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: recipientValid === true ? 'var(--lime-primary)' : 'rgba(74, 140, 24, 0.3)',
                  color: recipientValid === true ? '#1a1a1a' : 'rgba(255,255,255,0.5)',
                  cursor: recipientValid === true ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                  minWidth: 0
                }}
              >
                <Icon name="swap" color={recipientValid === true ? "primary" : "muted"} darkMode={darkMode} size={16} style={{ flexShrink: 0 }} />
                Transfer
              </button>
              <button 
                type="button" 
                onClick={() => {
                  setShowTransferModal(false);
                  setError('');
                  setTransferRecipient('');
                  setRecipientValid(null);
                  setRecipientUserInfo(null);
                }} 
                className="btn btn-secondary"
                style={{
                  flex: 1,
                  padding: '12px',
                  background: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  color: darkMode ? '#ffffff' : '#1a1a1a',
                  border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Service Comments Modal */}
      {showServiceCommentsModal && (
        <Modal onClose={() => setShowServiceCommentsModal(null)} darkMode={darkMode} title={
          <>
            <Icon name="chat" color="primary" darkMode={darkMode} size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            Szerviz Kommentek
          </>
        }>
          <div style={{ maxWidth: '500px' }}>
            {/* Info box */}
            <div style={{
              padding: '10px 14px',
              backgroundColor: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid rgba(255, 193, 7, 0.25)',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '12px',
              color: '#ff9800'
            }}>
              <Icon name="lock" color="warning" darkMode={darkMode} size={12} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              This service is locked. Description and price cannot be changed, but you can add comments anytime.
            </div>

            {/* Add comment form */}
            <form onSubmit={handleAddServiceComment} style={{ marginBottom: '20px' }}>
              <div style={{ marginBottom: '10px' }}>
                <textarea
                  value={newServiceComment}
                  onChange={(e) => setNewServiceComment(e.target.value)}
                  placeholder="Write a comment about the service..."
                  rows={3}
                  className="form-input"
                  style={{ width: '100%', resize: 'vertical' }}
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <select
                  value={serviceCommentCategory}
                  onChange={(e) => setServiceCommentCategory(e.target.value)}
                  className="form-input"
                  style={{ flex: '0 0 auto', padding: '6px 12px', fontSize: '13px' }}
                >
                  <option value="general">General</option>
                  <option value="follow_up">Follow-up</option>
                  <option value="issue">Issue</option>
                  <option value="note">Jegyzet</option>
                  <option value="question">Question</option>
                  <option value="resolution">Resolution</option>
                </select>
                <button type="submit" className="btn btn-primary" style={{ padding: '6px 14px', fontSize: '13px' }}>
                  Add
                </button>
              </div>
            </form>

            {/* Comments list */}
            <div>
              <h4 style={{ margin: '0 0 12px 0', color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
                Kommentek ({serviceComments.length})
              </h4>
              {loadingServiceComments ? (
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>Loading...</p>
              ) : serviceComments.length === 0 ? (
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', fontStyle: 'italic' }}>
                  No comments yet for this service.
                </p>
              ) : (
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {serviceComments.map(comment => (
                    <div key={comment.id} style={{
                      padding: '12px',
                      marginBottom: '8px',
                      backgroundColor: 'rgba(255,255,255,0.03)',
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.06)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '12px' }}>
                        <span style={{ color: '#3d5a20', fontWeight: '500' }}>
                          {comment.author_name || comment.author_username}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>
                          {new Date(comment.created_at).toLocaleString('en-GB')}
                        </span>
                      </div>
                      <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)', fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                        {comment.comment_text}
                      </p>
                      <span style={{
                        display: 'inline-block',
                        marginTop: '6px',
                        padding: '2px 6px',
                        backgroundColor: 'rgba(158, 158, 158, 0.15)',
                        borderRadius: '4px',
                        fontSize: '10px',
                        color: 'rgba(255,255,255,0.5)'
                      }}>
                        {comment.category || 'general'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <div 
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowReviewModal(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: darkMode ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div style={{
            background: darkMode ? '#1a1a3e' : '#ffffff',
            borderRadius: '20px',
            maxWidth: '450px',
            width: '100%',
            padding: '28px',
            border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Icon name="star" color="warning" darkMode={darkMode} size={48} style={{ marginBottom: '12px' }} />
              <h2 style={{ color: darkMode ? '#ffffff' : '#1a1a1a', margin: '0 0 8px 0', fontSize: '20px' }}>
                Rate your service
              </h2>
              {bike?.services?.find(s => s.id === showReviewModal) && (
                <p style={{ color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', margin: 0, fontSize: '14px' }}>
                  {bike.services.find(s => s.id === showReviewModal).company_name || 'Service'}
                </p>
              )}
            </div>

            {/* Star Rating */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewRating(star)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      fontSize: '36px',
                      cursor: 'pointer',
                      color: star <= reviewRating ? '#fbbf24' : 'rgba(255,255,255,0.2)',
                      transition: 'transform 0.15s, color 0.15s',
                      transform: star <= reviewRating ? 'scale(1.1)' : 'scale(1)'
                    }}
                  >
                    ★
                  </button>
                ))}
              </div>
              {reviewRating > 0 && (
                <div style={{ fontSize: '14px', color: '#fbbf24', fontWeight: '500' }}>
                  {reviewRating === 1 && 'Very poor'}
                  {reviewRating === 2 && 'Poor'}
                  {reviewRating === 3 && 'Average'}
                  {reviewRating === 4 && 'Good'}
                  {reviewRating === 5 && 'Excellent!'}
                </div>
              )}
            </div>

            {/* Comment */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '13px' }}>
                Note (optional)
              </label>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Describe your experience..."
                rows="3"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.2)',
                  background: darkMode ? 'rgba(0,0,0,0.3)' : '#ffffff',
                  color: darkMode ? '#ffffff' : '#1a1a1a',
                  fontSize: '14px',
                  resize: 'vertical',
                  minHeight: '80px'
                }}
              />
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => handleSubmitReview(showReviewModal)}
                className="btn btn-primary"
                disabled={reviewRating === 0 || submittingReview}
                style={{ flex: 1, padding: '14px' }}
              >
                {submittingReview ? (
                  <>
                    <Icon name="loading" color="primary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    Sending...
                  </>
                ) : (
                  <>
                    <Icon name="success" color="primary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    Submit review
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowReviewModal(null);
                  setReviewRating(0);
                  setReviewComment('');
                }}
                className="btn btn-secondary"
                style={{ padding: '14px 20px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Strava Activity Picker Modal */}
      {showStravaModal && (
        <div 
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowStravaModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div style={{
            background: '#1a1a3e',
            borderRadius: '20px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            {/* Header */}
            <div style={{ 
              padding: '24px 24px 16px',
              borderBottom: '1px solid rgba(255,255,255,0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ color: '#ffffff', margin: 0, fontSize: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Icon name="bike" color="primary" darkMode={darkMode} size={20} customColor="#fc4c02" style={{ verticalAlign: 'middle', marginRight: '10px' }} />
                  Add Strava Journey
                </h2>
                <button
                  onClick={() => setShowStravaModal(false)}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: 'none',
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: '4px 10px',
                    borderRadius: '6px'
                  }}
                >
                  ×
                </button>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.5)', margin: '8px 0 0', fontSize: '13px' }}>
                Select a ride from your Strava history to link to this bike passport
              </p>
            </div>

            {/* Content */}
            <div style={{ 
              padding: '16px 24px',
              overflowY: 'auto',
              flex: 1
            }}>
              {stravaActivitiesLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
                  <Icon name="loading" color="primary" darkMode={darkMode} size={32} style={{ marginBottom: '12px' }} />
                  Loading your Strava activities...
                </div>
              ) : availableStravaActivities.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
                  <Icon name="bike" color="muted" darkMode={darkMode} size={48} customColor="#fc4c02" style={{ marginBottom: '12px', opacity: 0.7 }} />
                  <p>No new cycling activities found</p>
                  <p style={{ fontSize: '13px' }}>All your recent rides are already linked to this bike</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {availableStravaActivities.map(activity => (
                    <div
                      key={activity.id}
                      onClick={() => handleAddStravaActivity(activity.id)}
                      style={{
                        padding: '14px',
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(252, 76, 2, 0.1)';
                        e.currentTarget.style.borderColor = 'rgba(252, 76, 2, 0.3)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div style={{ fontSize: '15px', fontWeight: '600', color: '#ffffff' }}>
                          {activity.name}
                        </div>
                        <span style={{
                          padding: '3px 8px',
                          background: 'rgba(252, 76, 2, 0.15)',
                          border: '1px solid rgba(252, 76, 2, 0.3)',
                          borderRadius: '12px',
                          color: '#fc4c02',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>
                          {activity.type}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>
                        <Icon name="calendar" color="secondary" darkMode={darkMode} size={12} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                        {new Date(activity.start_date_local).toLocaleDateString('en-US', {
                          weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
                        })}
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '16px',
                        fontSize: '12px',
                        color: 'rgba(255,255,255,0.7)'
                      }}>
                        <span><Icon name="map" color="secondary" darkMode={darkMode} size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{(activity.distance / 1000).toFixed(1)} km</span>
                        <span><Icon name="schedule" color="secondary" darkMode={darkMode} size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{Math.floor(activity.moving_time / 3600)}h {Math.floor((activity.moving_time % 3600) / 60)}m</span>
                        {activity.total_elevation_gain > 0 && (
                          <span><Icon name="terrain" color="secondary" darkMode={darkMode} size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{Math.round(activity.total_elevation_gain)} m</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowStravaModal(false)}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Service Detail Modal */}
      {selectedService && (
        <div 
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setSelectedService(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div style={{
            background: darkMode ? '#1a1a3e' : '#ffffff',
            borderRadius: '20px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            {/* Modal Header */}
            <div style={{ 
              padding: '24px',
              borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              position: 'sticky',
              top: 0,
              background: darkMode ? '#1a1a3e' : '#ffffff',
              borderRadius: '20px 20px 0 0'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h2 style={{ color: darkMode ? '#ffffff' : '#1a1a1a', margin: 0, fontSize: '22px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Icon name="service" color="primary" darkMode={darkMode} size={24} />
                    Service Details
                  </h2>
                  <p style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', margin: '8px 0 0', fontSize: '14px' }}>
                    {selectedService.service_date && new Date(selectedService.service_date).toLocaleDateString('en-GB', { 
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                    })}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedService(null)}
                  style={{
                    background: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                    border: 'none',
                    color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '4px 12px',
                    borderRadius: '8px'
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '24px' }}>
              {/* Status Badge */}
              <div style={{ marginBottom: '20px' }}>
                <span style={{
                  padding: '8px 16px',
                  background: selectedService.status === 'completed' 
                    ? 'rgba(34, 197, 94, 0.15)' 
                    : 'rgba(251, 191, 36, 0.15)',
                  border: `1px solid ${selectedService.status === 'completed' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(251, 191, 36, 0.3)'}`,
                  borderRadius: '20px',
                  color: selectedService.status === 'completed' ? '#22c55e' : '#fbbf24',
                  fontSize: '13px',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  {selectedService.status || 'Pending'}
                </span>
              </div>

              {/* Description */}
              {selectedService.description && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '12px', marginBottom: '8px', textTransform: 'uppercase' }}>Description</h3>
                  <p style={{ color: darkMode ? '#ffffff' : '#1a1a1a', fontSize: '15px', lineHeight: '1.6', margin: 0 }}>
                    {selectedService.description}
                  </p>
                </div>
              )}

              {/* Service Items - table format */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '12px', marginBottom: '12px', textTransform: 'uppercase' }}>Service Items</h3>
                {selectedService.items && selectedService.items.length > 0 ? (
                  <div style={{ 
                    border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    {/* Table header */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 60px 80px 80px',
                      gap: '8px',
                      padding: '10px 16px',
                      background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      <span>Item</span>
                      <span style={{ textAlign: 'center' }}>Qty</span>
                      <span style={{ textAlign: 'right' }}>Unit Price</span>
                      <span style={{ textAlign: 'right' }}>Total</span>
                    </div>
                    {/* Item rows */}
                    {selectedService.items.map((item, idx) => (
                      <div key={idx} style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 60px 80px 80px',
                        gap: '8px',
                        padding: '10px 16px',
                        borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                        fontSize: '14px'
                      }}>
                        <span style={{ color: darkMode ? 'rgba(255,255,255,0.85)' : '#1a1a1a' }}>
                          {item.item_name || item.name || 'Item'}
                        </span>
                        <span style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', textAlign: 'center' }}>
                          {item.quantity || 1}
                        </span>
                        <span style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', textAlign: 'right' }}>
                          {parseFloat(item.unit_price || 0).toFixed(2)}
                        </span>
                        <span style={{ color: '#22c55e', fontWeight: '600', textAlign: 'right' }}>
                          {parseFloat(item.total_price || item.price || 0).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    {/* Total row */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 60px 80px 80px',
                      gap: '8px',
                      padding: '10px 16px',
                      borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'}`,
                      background: darkMode ? 'rgba(34, 197, 94, 0.06)' : 'rgba(34, 197, 94, 0.06)',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      <span style={{ color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>Total</span>
                      <span></span>
                      <span></span>
                      <span style={{ color: '#22c55e', textAlign: 'right' }}>
                        {selectedService.items.reduce((sum, item) => sum + parseFloat(item.total_price || item.price || 0), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p style={{ color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', fontStyle: 'italic', margin: 0 }}>
                    No service items recorded
                  </p>
                )}
              </div>

              {/* Total Cost */}
              {selectedService.total_cost && parseFloat(selectedService.total_cost) > 0 && (
                <div style={{ 
                  marginBottom: '24px',
                  padding: '16px',
                  background: 'rgba(61, 90, 32, 0.1)',
                  border: '1px solid rgba(61, 90, 32, 0.2)',
                  borderRadius: '12px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px' }}>Total Cost</span>
                    <span style={{ color: 'var(--lime-primary)', fontSize: '24px', fontWeight: '700' }}>
                      £{parseFloat(selectedService.total_cost).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              {/* Company and Technician */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '12px', marginBottom: '12px', textTransform: 'uppercase' }}>Service Provider</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {selectedService.company_name && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Icon name="companies" color="secondary" darkMode={darkMode} size={18} />
                      <span style={{ color: darkMode ? '#ffffff' : '#1a1a1a', fontSize: '15px' }}>
                        {selectedService.company_name}
                      </span>
                    </div>
                  )}
                  {selectedService.technician_name && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Icon name="technician" color="secondary" darkMode={darkMode} size={18} />
                      <span style={{ color: darkMode ? '#ffffff' : '#1a1a1a', fontSize: '15px' }}>
                        Technician: {selectedService.technician_name}
                      </span>
                    </div>
                  )}
                  {selectedService.service_time && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Icon name="schedule" color="secondary" darkMode={darkMode} size={18} />
                      <span style={{ color: darkMode ? '#ffffff' : '#1a1a1a', fontSize: '15px' }}>
                        Time: {new Date(`2000-01-01T${selectedService.service_time}`).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Receipts */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '12px', margin: 0, textTransform: 'uppercase' }}>Receipts</h3>
                  {isOwner && (
                    <button
                      onClick={() => setShowReceiptUpload(!showReceiptUpload)}
                      className="btn btn-secondary"
                      style={{ fontSize: '12px', padding: '6px 12px' }}
                    >
                      <Icon name="upload" color="secondary" darkMode={darkMode} size={14} style={{ marginRight: '6px' }} />
                      {showReceiptUpload ? 'Cancel' : 'Upload Receipt'}
                    </button>
                  )}
                </div>
                
                {/* Existing receipts */}
                {selectedService.receipts && selectedService.receipts.length > 0 && (
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: showReceiptUpload ? '16px' : 0 }}>
                    {selectedService.receipts.map((receipt, idx) => {
                      const receiptUrl = receipt.file_url || receipt.url || receipt.path;
                      const isImage = receipt.file_type?.startsWith('image') || 
                                     ['jpg', 'jpeg', 'png', 'gif', 'webp'].some(ext => receiptUrl?.toLowerCase().endsWith(ext));
                      
                      return (
                        <button
                          type="button"
                          key={idx}
                          onClick={() => isImage ? setReceiptModalImage(receiptUrl) : window.open(receiptUrl, '_blank')}
                          style={{ 
                            background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px',
                            borderRadius: '10px',
                            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                            transition: 'all 0.2s ease',
                            maxWidth: '120px',
                            cursor: 'pointer'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
                            e.currentTarget.style.borderColor = darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)';
                            e.currentTarget.style.borderColor = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
                          }}
                        >
                          {isImage ? (
                            <img 
                              src={receiptUrl}
                              alt={`Receipt ${idx + 1}`}
                              style={{ 
                                width: '100px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '6px',
                                border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '100px',
                              height: '80px',
                              background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Icon name="documents" color="secondary" darkMode={darkMode} size={32} />
                            </div>
                          )}
                          <span style={{ 
                            color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', 
                            fontSize: '11px',
                            textAlign: 'center'
                          }}>
                            Receipt {idx + 1}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Receipt upload section */}
                {showReceiptUpload && isOwner && (
                  <div style={{
                    background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                    border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                    borderRadius: '12px',
                    padding: '16px'
                  }}>
                    <p style={{ color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', fontSize: '13px', marginBottom: '12px' }}>
                      Upload a receipt or invoice image. Our AI can extract service items and prices automatically.
                    </p>
                    
                    {/* File inputs - separate camera and gallery for iOS compatibility */}
                    <div style={{ marginBottom: '12px' }}>
                      {/* Hidden camera input (with capture for iOS) */}
                      <input
                        type="file"
                        accept="image/*,image/heic,image/heif"
                        capture="environment"
                        onChange={handleReceiptFileChange}
                        style={{ display: 'none' }}
                        id="receipt-camera-input"
                      />
                      {/* Hidden gallery input (no capture, allows HEIC selection) */}
                      <input
                        type="file"
                        accept="image/*,image/heic,image/heif,.heic,.heif,.pdf"
                        onChange={handleReceiptFileChange}
                        style={{ display: 'none' }}
                        id="receipt-gallery-input"
                      />
                      
                      {receiptFile ? (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 16px',
                          background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                          border: `1px solid ${darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'}`,
                          borderRadius: '8px',
                          fontSize: '14px',
                          color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                        }}>
                          <Icon name="success" customColor="#4A8C18" size={16} />
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {receiptFile.name}
                          </span>
                          <span style={{ fontSize: '12px', opacity: 0.6 }}>
                            {(receiptFile.size / 1024).toFixed(0)} KB
                          </span>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                          <label
                            htmlFor="receipt-camera-input"
                            className="btn btn-primary"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '12px 20px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '500',
                              minHeight: '48px',
                            }}
                          >
                            <Icon name="camera" size={18} color="white" darkMode={true} />
                            Scan Receipt
                          </label>
                          <label
                            htmlFor="receipt-gallery-input"
                            className="btn btn-secondary"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '12px 20px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '500',
                              minHeight: '48px',
                            }}
                          >
                            <Icon name="image" size={18} color="gray" darkMode={darkMode} />
                            From Gallery
                          </label>
                        </div>
                      )}
                    </div>

                    {/* Preview */}
                    {receiptPreview && (
                      <div style={{ marginBottom: '12px' }}>
                        <img 
                          src={receiptPreview} 
                          alt="Receipt preview" 
                          style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}
                        />
                      </div>
                    )}

                    {/* AI Extraction button */}
                    {receiptFile && !extractedItems && (
                      <button
                        onClick={handleExtractFromReceipt}
                        disabled={extractingReceipt}
                        className="btn btn-secondary"
                        style={{ marginRight: '10px', fontSize: '13px' }}
                      >
                        <Icon name="service" color="secondary" darkMode={darkMode} size={14} style={{ marginRight: '6px' }} />
                        {extractingReceipt ? 'Reading receipt with AI... (~15s)' : 'Extract with AI'}
                      </button>
                    )}

                    {/* Editable extracted items */}
                    {extractedItems && (
                      <div style={{
                        background: darkMode ? 'rgba(61, 90, 32, 0.1)' : 'rgba(61, 90, 32, 0.05)',
                        border: '1px solid rgba(61, 90, 32, 0.2)',
                        borderRadius: '10px',
                        padding: '12px',
                        marginBottom: '12px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', flexWrap: 'wrap', gap: '6px' }}>
                          <h4 style={{ color: '#4A8C18', fontSize: '12px', margin: 0, textTransform: 'uppercase' }}>
                            <Icon name="success" customColor="#4A8C18" size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                            Extracted Data — Edit if needed
                          </h4>
                          <button
                            type="button"
                            onClick={() => {
                              setExtractedItems(null);
                              setSuccess('');
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                              fontSize: '11px',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              padding: '2px 4px'
                            }}
                          >
                            Re-extract
                          </button>
                        </div>

                        {/* Editable items list */}
                        {extractedItems.service_items?.length > 0 && (
                          <div style={{ marginBottom: '10px' }}>
                            <strong style={{ color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Items ({extractedItems.service_items.length})
                            </strong>

                            {extractedItems.service_items.map((item, idx) => (
                              <div key={idx} style={{
                                padding: '10px 0',
                                borderBottom: idx < extractedItems.service_items.length - 1 ? `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` : 'none'
                              }}>
                                {/* Item name row */}
                                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                                  <input
                                    type="text"
                                    value={item.description || item.name || ''}
                                    onChange={(e) => {
                                      const updated = [...extractedItems.service_items];
                                      updated[idx] = { ...updated[idx], description: e.target.value, name: e.target.value };
                                      setExtractedItems(prev => ({ ...prev, service_items: updated }));
                                    }}
                                    placeholder="Item name"
                                    style={{
                                      background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                                      border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                                      borderRadius: '6px',
                                      color: darkMode ? '#fff' : '#1a1a1a',
                                      padding: '8px 10px',
                                      fontSize: '14px',
                                      flex: 1,
                                      minWidth: 0
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updated = extractedItems.service_items.filter((_, i) => i !== idx);
                                      setExtractedItems(prev => ({ ...prev, service_items: updated }));
                                    }}
                                    style={{
                                      background: 'rgba(239, 68, 68, 0.1)',
                                      border: '1px solid rgba(239, 68, 68, 0.2)',
                                      borderRadius: '6px',
                                      color: '#ef4444',
                                      cursor: 'pointer',
                                      fontSize: '16px',
                                      padding: '0',
                                      lineHeight: '1',
                                      flexShrink: 0,
                                      minWidth: '36px',
                                      minHeight: '36px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    title="Remove item"
                                  >
                                    ×
                                  </button>
                                </div>
                                {/* Qty, Unit Price, Total row */}
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '10px', color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', marginBottom: '3px', textTransform: 'uppercase', fontWeight: '600' }}>Qty</div>
                                    <input
                                      type="number"
                                      value={item.quantity || 1}
                                      min="1"
                                      step="1"
                                      onChange={(e) => {
                                        const updated = [...extractedItems.service_items];
                                        const qty = parseFloat(e.target.value) || 1;
                                        const unitPrice = parseFloat(updated[idx].unit_price) || 0;
                                        updated[idx] = { ...updated[idx], quantity: qty, total: qty * unitPrice };
                                        setExtractedItems(prev => ({ ...prev, service_items: updated }));
                                      }}
                                      style={{
                                        background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                                        border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                                        borderRadius: '6px',
                                        color: darkMode ? '#fff' : '#1a1a1a',
                                        padding: '8px 6px',
                                        fontSize: '14px',
                                        textAlign: 'center',
                                        width: '100%'
                                      }}
                                    />
                                  </div>
                                  <div style={{ flex: 1.5, minWidth: 0 }}>
                                    <div style={{ fontSize: '10px', color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', marginBottom: '3px', textTransform: 'uppercase', fontWeight: '600' }}>Unit Price</div>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={item.unit_price || 0}
                                      onChange={(e) => {
                                        const updated = [...extractedItems.service_items];
                                        const price = parseFloat(e.target.value) || 0;
                                        const qty = parseFloat(updated[idx].quantity) || 1;
                                        updated[idx] = { ...updated[idx], unit_price: price, total: qty * price };
                                        setExtractedItems(prev => ({ ...prev, service_items: updated }));
                                      }}
                                      style={{
                                        background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                                        border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                                        borderRadius: '6px',
                                        color: darkMode ? '#fff' : '#1a1a1a',
                                        padding: '8px 6px',
                                        fontSize: '14px',
                                        textAlign: 'right',
                                        width: '100%'
                                      }}
                                    />
                                  </div>
                                  <div style={{ flex: 1.5, minWidth: 0 }}>
                                    <div style={{ fontSize: '10px', color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', marginBottom: '3px', textTransform: 'uppercase', fontWeight: '600' }}>Total</div>
                                    <div style={{
                                      padding: '8px 6px',
                                      fontSize: '14px',
                                      fontWeight: '700',
                                      color: '#4A8C18',
                                      textAlign: 'right'
                                    }}>
                                      {(parseFloat(item.total) || (parseFloat(item.quantity || 1) * parseFloat(item.unit_price || 0))).toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}

                            {/* Add item button */}
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...(extractedItems.service_items || []), { description: '', name: '', quantity: 1, unit_price: 0, total: 0 }];
                                setExtractedItems(prev => ({ ...prev, service_items: updated }));
                              }}
                              style={{
                                marginTop: '6px',
                                background: 'none',
                                border: `1px dashed ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                                borderRadius: '6px',
                                color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                padding: '8px 12px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                width: '100%',
                                minHeight: '40px'
                              }}
                            >
                              + Add item
                            </button>
                          </div>
                        )}

                        {/* Editable total cost */}
                        <div style={{
                          paddingTop: '10px',
                          borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '150px' }}>
                              <span style={{ color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' }}>Total Cost</span>
                              <input
                                type="number"
                                step="0.01"
                                value={extractedItems.total_cost || 0}
                                onChange={(e) => {
                                  setExtractedItems(prev => ({ ...prev, total_cost: parseFloat(e.target.value) || 0 }));
                                }}
                                style={{
                                  background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                                  border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                                  borderRadius: '6px',
                                  color: '#4A8C18',
                                  padding: '8px 10px',
                                  fontSize: '16px',
                                  fontWeight: '700',
                                  textAlign: 'right',
                                  width: '120px',
                                  maxWidth: '100%'
                                }}
                              />
                            </div>
                            {extractedItems.service_items?.length > 0 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const itemsTotal = extractedItems.service_items.reduce((sum, item) => {
                                    return sum + (parseFloat(item.total) || (parseFloat(item.quantity || 1) * parseFloat(item.unit_price || 0)));
                                  }, 0);
                                  setExtractedItems(prev => ({ ...prev, total_cost: parseFloat(itemsTotal.toFixed(2)) }));
                                }}
                                style={{
                                  background: 'rgba(74, 140, 24, 0.15)',
                                  border: '1px solid rgba(74, 140, 24, 0.3)',
                                  borderRadius: '6px',
                                  color: '#4A8C18',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  padding: '6px 10px',
                                  fontWeight: '600',
                                  whiteSpace: 'nowrap',
                                  minHeight: '32px'
                                }}
                              >
                                Sync from items
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Upload button */}
                    {receiptFile && (
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          onClick={handleUploadReceipt}
                          disabled={uploadingReceipt}
                          className="btn btn-primary"
                          style={{ fontSize: '13px' }}
                        >
                          <Icon name="upload" customColor="#ffffff" size={14} style={{ marginRight: '6px' }} />
                          {uploadingReceipt ? 'Uploading...' : (extractedItems ? 'Save Receipt & Items' : 'Upload Receipt')}
                        </button>
                        <button
                          onClick={clearReceiptUpload}
                          className="btn btn-secondary"
                          style={{ fontSize: '13px' }}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* No receipts message */}
                {(!selectedService.receipts || selectedService.receipts.length === 0) && !showReceiptUpload && (
                  <p style={{ color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', fontStyle: 'italic', margin: 0, fontSize: '14px' }}>
                    No receipts uploaded
                  </p>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ 
              padding: '16px 24px',
              borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => { setSelectedService(null); clearReceiptUpload(); }}
                className="btn btn-primary"
                style={{ padding: '12px 24px' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Image Modal - fullscreen overlay */}
      {receiptModalImage && (
        <div
          onClick={() => setReceiptModalImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.92)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
            padding: '20px'
          }}
        >
          {/* Close button */}
          <button
            onClick={() => setReceiptModalImage(null)}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#fff',
              fontSize: '20px',
              fontWeight: 'bold',
              zIndex: 10001,
              backdropFilter: 'blur(8px)'
            }}
          >
            x
          </button>
          {/* Download link */}
          <a
            href={receiptModalImage}
            download
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: '16px',
              right: '68px',
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#fff',
              fontSize: '16px',
              textDecoration: 'none',
              backdropFilter: 'blur(8px)'
            }}
            title="Download"
          >
            <Icon name="documents" color="white" darkMode={true} size={18} />
          </a>
          {/* Receipt image */}
          <img
            src={receiptModalImage}
            alt="Receipt"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: '8px',
              cursor: 'default',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
            }}
          />
        </div>
      )}
    </Layout>
  );
}

// Simple Modal Component
function Modal({ onClose, title, children, darkMode = true }) {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: darkMode ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.4)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: '24px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: darkMode ? '#1a1a3e' : '#ffffff',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
          borderRadius: '20px',
          padding: '28px',
          maxWidth: '400px',
          width: '100%',
          boxShadow: darkMode ? '0 24px 60px rgba(0,0,0,0.5)' : '0 24px 60px rgba(0,0,0,0.15)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', color: darkMode ? '#ffffff' : '#1a1a1a' }}>{title}</h2>
          <button
            onClick={onClose}
            style={{
              background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '4px 10px',
              borderRadius: '6px',
              color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)'
            }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Simple Review Form
function ReviewForm({ onSubmit, companyName, darkMode = true }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [showForm, setShowForm] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (rating === 0) return;
    onSubmit({ rating, comment });
    setComment('');
    setRating(0);
    setShowForm(false);
  };

  if (!showForm) {
    return (
      <div style={{
        padding: '16px',
        background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.08) 0%, rgba(245, 158, 11, 0.04) 100%)',
        border: '1px solid rgba(251, 191, 36, 0.2)',
        borderRadius: '12px',
        textAlign: 'center'
      }}>
        <Icon name="star" color="warning" darkMode={darkMode} size={24} style={{ marginBottom: '8px' }} />
        <p style={{ margin: '0 0 12px 0', color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
          {companyName ? `Were you satisfied with ${companyName}'s service?` : 'Were you satisfied with the service?'}
        </p>
        <p style={{ margin: '0 0 12px 0', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
          Help others with your review!
        </p>
        <button 
          onClick={() => setShowForm(true)}
          className="btn btn-primary"
          style={{ fontSize: '13px', padding: '8px 20px' }}
        >
          <Icon name="write" color="primary" darkMode={darkMode} size={13} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
          Értékelés írása
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{
      padding: '16px',
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '12px'
    }}>
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', color: 'rgba(255,255,255,0.8)', fontSize: '14px', fontWeight: '500' }}>
          How satisfied were you?
        </label>
        <div style={{ display: 'flex', gap: '6px' }}>
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                color: star <= rating ? '#fbbf24' : 'rgba(255,255,255,0.2)',
                transition: 'transform 0.15s, color 0.15s',
                transform: star <= rating ? 'scale(1.1)' : 'scale(1)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = star <= rating ? 'scale(1.1)' : 'scale(1)'}
            >
              ★
            </button>
          ))}
        </div>
        {rating > 0 && (
          <div style={{ marginTop: '8px', fontSize: '13px', color: '#fbbf24' }}>
            {rating === 1 && 'Very Poor'}
            {rating === 2 && 'Poor'}
            {rating === 3 && 'Average'}
            {rating === 4 && 'Good'}
            {rating === 5 && 'Excellent!'}
          </div>
        )}
      </div>
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
          Comment (optional)
        </label>
        <textarea
          placeholder="Describe your experience..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows="3"
          style={{ 
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(0,0,0,0.3)',
            color: '#ffffff',
            fontSize: '14px',
            resize: 'vertical',
            minHeight: '80px'
          }}
        />
      </div>
      <div style={{ display: 'flex', gap: '10px' }}>
        <button 
          type="submit" 
          className="btn btn-primary" 
          style={{ fontSize: '13px', padding: '10px 20px' }}
          disabled={rating === 0}
        >
          ✓ Submit Review
        </button>
        <button 
          type="button" 
          onClick={() => setShowForm(false)}
          className="btn btn-secondary" 
          style={{ fontSize: '13px', padding: '10px 16px' }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default BikeDetailPage;
